import React from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import Picture from '../components/Picture';
import SEO from '../components/SEO';
import { Facebook, Instagram } from 'lucide-react';

const HERO_IMG = '/images/hero-glossfest.jpg';
const DO_MORE_BANNER = '/images/do_more_big.jpg';
const COMMITTEE_IMG = '/images/committee.jpg';

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <SEO
        description="Glossop & District Round Table 916 — fun, friendship and fundraising in Glossop, Derbyshire. Join 30,000 men worldwide making a difference through community events, Bonfire Night, Heritage Weekend and more."
        path="/"
        keywords={['Glossop Round Table', 'join Round Table Glossop', 'Glossop community events', 'Glossop Derbyshire', 'fun friendship fundraising']}
      />
      {/* Hero */}
      <HeroSection
        image={HERO_IMG}
        title="Fun, Friendship & Fundraising"
        subtitle="Glossop & District Round Table 916 — It's time to Do More"
        subtitleBold
        contentAlign="top"
      >
        <Link
          to="/membership"
          data-testid="hero-join-btn"
          className="btn-amber mt-8 inline-block"
        >
          Join Round Table
        </Link>
      </HeroSection>

      {/* About Section */}
      <section data-testid="home-about-section" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
                About Round Table
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] mb-6">
                Round Table is fun, friendship and fundraising
              </h2>
              <p className="text-base text-[#52525B] leading-relaxed mb-6">
                Founded in 1927 by Louis Marchesi, Round Tables meet all over Great Britain and Ireland to try white water rafting, hiking, zorbing, or perhaps go for a round of crazy golf, learn how to make the perfect sushi or take a tour of the local brewery. Round Table is the perfect way to make your weeknights as good as your weekends – It's time to end the boredom and Do More!
              </p>
              <Link
                to="/about"
                data-testid="home-learn-more-link"
                className="text-sm font-bold uppercase tracking-wide text-[#F0A500] hover:text-[#D99500] transition-colors inline-flex items-center gap-2"
              >
                Learn more about us
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
            <div className="relative">
              <Picture
                src={COMMITTEE_IMG}
                alt="Glossop Round Table members"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Do More Banner */}
      <DoMoreBanner />

      {/* Members' Highlights — Woodland Games 2023 teaser */}
      <section data-testid="home-highlights-section" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <Picture
                src="/images/woodland-games-2023-1.jpg"
                alt="Glossop Round Table members around the campfire at the Woodland Games 2023"
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
                Members&apos; Highlights
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] mb-6">
                Woodland Games 2023
              </h2>
              <p className="text-base text-[#52525B] leading-relaxed mb-6">
                Paintball, axe throwing, archery, tug-of-war and an open-fire feast under the stars — a weekend that summed up exactly why Glossop Round Tablers keep coming back for more. Take a peek at the gallery and see what your weekends could look like.
              </p>
              <Link
                to="/socials"
                data-testid="home-highlights-link"
                className="text-sm font-bold uppercase tracking-wide text-[#F0A500] hover:text-[#D99500] transition-colors inline-flex items-center gap-2"
              >
                See the Woodland Games gallery
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fundraising Moments strip — 3 Heritage Weekend banners */}
      <FundraisingMomentsStrip />

      {/* Facebook Live Feed */}
      <FacebookFeedSection />

      {/* Charities Section */}
      <section data-testid="home-charities-section" className="py-20 sm:py-28 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
            Making a Difference
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] mb-6 max-w-3xl mx-auto">
            Proud to support charities big and small
          </h2>
          <p className="text-base text-[#52525B] leading-relaxed max-w-2xl mx-auto mb-10">
            Round Table are proud to support national charities like Children in Need and Anthony Nolan, whilst also supporting thousands of local charities and individuals making an enormous difference to millions of lives every year.
          </p>
          <Link
            to="/membership"
            data-testid="home-join-cta-btn"
            className="btn-amber inline-block"
          >
            Be Part of Something More
          </Link>
        </div>
      </section>
    </div>
  );
}

function DoMoreBanner() {
  const id = React.useId().replace(/:/g, '');
  const cls = `do-more-${id}`;
  const jpg = DO_MORE_BANNER;
  const webp = jpg.replace(/\.(jpe?g|png)$/i, '.webp');
  const css = `.${cls}{background-image:url("${jpg}");background-image:image-set(url("${webp}") type("image/webp"),url("${jpg}") type("image/jpeg"));}`;
  return (
    <section
      data-testid="home-domore-section"
      className={`hero-parallax-short ${cls}`}
      style={{ minHeight: '50vh' }}
    >
      <style>{css}</style>
      <div className="hero-overlay" style={{ background: 'rgba(10, 10, 10, 0.4)' }} />
      <div className="relative z-10 flex flex-col items-center justify-center h-full min-h-[50vh] text-center px-4">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
          Raising Over &pound;3.3 Million a Year
        </h2>
        <p className="text-base sm:text-lg text-white font-bold [text-shadow:0_2px_8px_rgba(0,0,0,0.6)] max-w-2xl leading-relaxed">
          From the beer festivals, Firework events and Santa Sleighs, to climbing Mount Kilimanjaro and 10k fun runs, Round Table clubs make an enormous difference to millions of lives every year.
        </p>
      </div>
    </section>
  );
}

const FUNDRAISING_MOMENTS = [
  {
    src: '/images/heritage-norfolk-sq.jpg',
    alt: 'Crowds enjoying an evening in Glossop\'s Norfolk Square during Heritage Weekend 2026',
    caption: 'Norfolk Square after dark',
  },
  {
    src: '/images/heritage-brass-band.jpg',
    alt: 'The Marple Band playing live on the Heritage Weekend main stage with Round Table volunteers in orange t-shirts in the foreground',
    caption: 'Live music on the main stage',
  },
  {
    src: '/images/heritage-plane.jpg',
    alt: 'Round Table volunteers posing in front of a Spitfire display on Glossop high street during Heritage Weekend',
    caption: 'Military heritage on the high street',
  },
];

function FundraisingMomentsStrip() {
  return (
    <section
      data-testid="home-fundraising-strip"
      className="py-12 sm:py-16 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">
              Fundraising moments
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
              Scenes from Heritage Weekend 2026
            </h2>
          </div>
          <Link
            to="/heritage-programme"
            data-testid="home-fundraising-strip-link"
            className="hidden sm:inline-flex text-xs font-bold uppercase tracking-wide text-[#F0A500] hover:text-white transition-colors items-center gap-2 pb-1"
          >
            See the full programme
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {FUNDRAISING_MOMENTS.map((m) => (
            <figure
              key={m.src}
              data-testid={`fundraising-moment-${m.src.split('/').pop().replace(/\.[^.]+$/, '')}`}
              className="relative group overflow-hidden"
            >
              <Picture
                src={m.src}
                alt={m.alt}
                className="w-full h-48 sm:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3 text-[11px] sm:text-xs font-bold uppercase tracking-[0.15em] text-white">
                {m.caption}
              </figcaption>
            </figure>
          ))}
        </div>
        <Link
          to="/heritage-programme"
          data-testid="home-fundraising-strip-link-mobile"
          className="sm:hidden mt-6 inline-flex text-xs font-bold uppercase tracking-wide text-[#F0A500] hover:text-white transition-colors items-center gap-2"
        >
          See the full programme
          <span aria-hidden="true">&rarr;</span>
        </Link>
      </div>
    </section>
  );
}

const FB_PAGE_URL = 'https://www.facebook.com/glossoprt';
const INSTAGRAM_URL = 'https://www.instagram.com/glossoprt/';
const TIKTOK_URL = 'https://www.tiktok.com/@glossoprt';

function FacebookFeedSection() {
  // The official Facebook Page Plugin no longer reliably renders post content
  // for unauthenticated visitors (the timeline tab shows only the header chrome
  // and a never-ending spinner). Rather than expose users to that broken UX,
  // we render a polished "Follow us" CTA that drives traffic to the page
  // directly — same goal, 100% reliable.
  return (
    <section
      data-testid="home-facebook-section"
      className="py-20 sm:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
              Stay Connected
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] mb-6">
              Follow our story online
            </h2>
            <p className="text-base text-[#52525B] leading-relaxed mb-6">
              We post regularly with what we&apos;re up to in the community &mdash; event nights, fundraising totals, behind-the-scenes from the Bonfire build week and the occasional live update. Follow us on Facebook or Instagram to see it all as it happens.
            </p>
            <a
              href={FB_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-facebook-page-link"
              className="text-sm font-bold uppercase tracking-wide text-[#F0A500] hover:text-[#D99500] transition-colors inline-flex items-center gap-2"
            >
              Visit our Facebook page
              <span aria-hidden="true">&rarr;</span>
            </a>
          </div>
          <div className="flex justify-center lg:justify-end">
            <div
              data-testid="home-social-card"
              className="w-full max-w-[500px] bg-[#0A0A0A] text-white p-8 sm:p-10 border-l-4 border-[#F0A500]"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#1877F2] flex items-center justify-center flex-shrink-0">
                  <Facebook className="w-8 h-8 text-white" fill="currentColor" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest font-bold text-[#F0A500] mb-1">On Facebook</div>
                  <div className="text-lg font-bold leading-tight">Glossop &amp; District Round Table</div>
                  <div className="text-xs text-zinc-400 mt-0.5">@glossoprt &middot; 1.8K followers</div>
                </div>
              </div>

              <p className="text-sm text-zinc-300 leading-relaxed mb-6">
                Photos from our latest events, fundraising updates, and the occasional live broadcast from Bonfire Night, GlossFest and the Heritage Weekend. Give us a follow and never miss a beat.
              </p>

              <a
                href={FB_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-social-card-fb-btn"
                className="block w-full text-center px-6 py-3 bg-[#1877F2] hover:bg-[#1465D6] text-white font-bold uppercase tracking-wider text-sm transition-colors mb-3"
              >
                Follow on Facebook
              </a>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-social-card-ig-btn"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                  Instagram
                </a>
                <a
                  href={TIKTOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-social-card-tt-btn"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  TikTok
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
